import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Result } from '@unimarket/shared';

import { CartProduct } from '../../model/cart-product';
import { StorefrontPage } from '../../model/storefront-page';
import { CartService } from '../../service/cart.service';
import { SeoService } from '../../service/seo.service';
import { MoneyPipe } from '../../money.pipe';

function atLeastOne(quantity: string): number {
  const value = Math.floor(Number(quantity));
  return value > 0 ? value : 1;
}

/**
 * The `cart` template. "Оновити кількості" only appears once a quantity was actually edited, the
 * way the Mustache page swapped the checkout button for it.
 */
@Component({
  selector: 'cart-page',
  imports: [RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  template: `
    <!-- Title Page -->
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h1 class="l-text2 t-center">
        Кошик
      </h1>
    </section>

    <!-- Cart -->
    <section class="cart bgwhite p-t-70 p-b-100">
      <div class="container">
        @if (lastChange().error; as refusal) {
          <p class="text-center m-text8 p-b-20">{{ refusal }}</p>
        }
        @if (cart().products.length) {
          <div class="row">
            <div class="col-lg">
              <!-- Cart item -->
              <div class="container-table-cart pos-relative">
                <div class="wrap-table-shopping-cart bgwhite">
                  <table class="table-shopping-cart">
                    <tbody>
                    <tr class="table-head">
                      <th class="column-1"></th>
                      <th class="column-2">Товар</th>
                      <th class="column-3">Ціна</th>
                      <th class="column-4 text-center">Кількість</th>
                      <th class="column-3 text-center">Сума</th>
                      <th class="column-5 text-center"></th>
                    </tr>

                      @for (product of cart().products; track product.productId) {
                        <tr class="table-row">
                          <td class="column-1 pl-3 pr-3 pl-md-5">
                            <div class="cart-img-product b-rad-4 o-f-hidden">
                              <button style="right: -14px; position: absolute; top: -19px;"
                                      (click)="remove(product.productId)">
                                <span class="fa fa-lg fa-times-circle"></span>
                              </button>
                              <a [routerLink]="product.uri">
                                @if (product.photo) {
                                  <img [src]="product.photo" [alt]="product.name">
                                }
                              </a>
                            </div>
                          </td>
                          <td class="column-2">
                            <a [routerLink]="product.uri">
                              {{ product.name }}
                            </a>
                          </td>
                          <td class="column-3">{{ product.price | money }}</td>
                          <td class="column-4" style="text-align: -webkit-center;">
                            <div class="flex-w bo5 of-hidden w-size17">
                              <button class="btn-num-product-down color1 flex-c-m size7 bg8 eff2"
                                      (click)="change(product.productId, -1)">
                                <i class="fs-12 fa fa-minus" aria-hidden="true"></i>
                              </button>
                              <input class="size8 m-text18 t-center num-product" type="number"
                                     min="1" [name]="product.productId" [value]="quantityOf(product)"
                                     (input)="edit(product.productId, $any($event.target).value)"
                                     (change)="setQuantity(product.productId, $any($event.target))">
                              <button class="btn-num-product-up color1 flex-c-m size7 bg8 eff2"
                                      (click)="change(product.productId, 1)">
                                <i class="fs-12 fa fa-plus" aria-hidden="true"></i>
                              </button>
                            </div>
                          </td>
                          <td class="column-3 text-center">{{ product.total | money }}</td>
                          <td class="column-5 text-center">
                            <button (click)="remove(product.productId)">
                              <span class="fa fa-lg fa-trash default-text-color"></span>
                            </button>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="flex-w flex-sb-m p-t-25 p-b-25 bo8 p-l-35 p-r-60 p-lr-15-sm justify-content-end">
                @if (edited()) {
                  <div class="size11 trans-0-4 m-t-10 m-b-10">
                    <button id="update-quantities" class="flex-c-m sizefull bg1 bo-rad-23 hov1 s-text1 trans-0-4"
                            (click)="updateQuantity()">
                      Оновити кількості
                    </button>
                  </div>
                } @else {
                  <div class="size11 trans-0-4 m-t-10 m-b-10">
                    <a id="checkout" routerLink="/checkout"
                       class="flex-c-m sizefull bglight bo-rad-23 hov1 s-text1 trans-0-4">
                      Продовжити
                    </a>
                  </div>
                }
              </div>
            </div>
          </div>
        } @else {
          <p class="text-center">
            Ваш кошик пустий. <a class="fglight" routerLink="/products">Додати товари?</a>
          </p>
        }
      </div>
    </section>
  `
})
export class CartPageComponent {

  readonly data = input.required<StorefrontPage>();
  private cartService = inject(CartService);
  private quantities = signal<Record<number, number>>({});

  readonly cart = this.cartService.cart;
  readonly lastChange = signal(Result.ok());
  private readonly changes = computed(() => Object.fromEntries(this.cart().products
    .filter(product => this.quantityOf(product) !== product.quantity)
    .map(product => [product.productId, this.quantityOf(product)])));
  readonly edited = computed(() => Object.keys(this.changes()).length > 0);

  constructor() {
    inject(SeoService).page(this.data, true);
  }

  quantityOf(product: CartProduct) {
    return this.quantities()[product.productId] ?? product.quantity;
  }

  edit(productId: number, quantity: string) {
    if (!/^[0-9]+$/.test(quantity) || Number(quantity) < 1) {
      return;
    }
    this.quantities.update(current => ({ ...current, [productId]: Number(quantity) }));
  }

  setQuantity(productId: number, box: HTMLInputElement) {
    box.value = String(atLeastOne(box.value));
    this.edit(productId, box.value);
  }

  change(productId: number, delta: number) {
    const product = this.cart().products.find(item => item.productId === productId);
    if (product) {
      this.edit(productId, String(Math.max(1, this.quantityOf(product) + delta)));
    }
  }

  async updateQuantity() {
    const result = await this.cartService.changeQuantities(this.changes());
    this.lastChange.set(result);
    if (result.ok) {
      this.quantities.set({});
    }
  }

  async remove(productId: number) {
    const result = await this.cartService.remove(productId);
    this.lastChange.set(result);
  }

}
